"use strict";

import angular from "angular";

import template from "./chart-historic.component.html";
import "./chart-historic.component.scss";

import _ from "lodash";
import { Point } from "../../../gateway/Point/Point";
import { PointCalculated } from "../../../gateway/PointCalculated/PointCalculated";
import { Serie } from "../../../gateway/Serie/Serie";
import { AvailablePeriod } from "../../../presentation/services/AvailablePeriod/AvailablePeriod";
import { CalculatingMachine } from "../../../presentation/services/CalculatingMachine/CalculatingMachine";

import { ChartStructure } from "../../../presentation/services/ChartStructure/ChartStructure";
import { ColorService } from "../../../presentation/services/ColorService/ColorService";
import { FormatUnits } from "../../../presentation/services/FormatUnits/FormatUnits";
import * as DateUtils from "../../../utils/date/date";

class ChartHistoricComponent {
    constructor($state, $filter, ThemesService) {
        this.SerieDomain = new Serie();
        this.PointDomain = new Point();
        this.PointCalculatedDomain = new PointCalculated();
        this.CalculatingMachine = new CalculatingMachine({
            Serie: this.SerieDomain,
            Point: this.PointDomain,
            PointCalculated: this.PointCalculatedDomain
        });
        this.FormatUnits = new FormatUnits($filter);
        this.ChartStructure = new ChartStructure({
            FormatUnits: this.FormatUnits,
            ThemesService: ThemesService
        });
        this.ColorService = new ColorService({ ThemesService: ThemesService });
        this.AvailablePeriod = new AvailablePeriod();

        this.color = "";
        this.polarityColor = "";
        this.companyId = $state.params.id;
    }

    $onInit() {
        this._setVisualization();
    }

    $onChanges(changes) {
        if (this.fakeData && _.get(changes, "series.currentValue.length"))
            this._setVisualization();

        if (
            this.fakeData &&
            Array.isArray(_.get(changes, "series.previousValue")) &&
            _.get(changes, "series.currentValue.length")
        )
            this._setVisualization();

        if (
            !_.isEmpty(_.get(changes, "currentPeriod.previousValue")) &&
            changes.currentPeriod
        )
            this._setVisualization();

        if (
            !_.isEmpty(_.get(changes, "currentComparation.previousValue")) &&
            changes.currentComparation
        )
            this._setVisualization();
    }

    _setColor() {
        if (_.get(this.historic, "color")) {
            this.color = this.ColorService.colorByPath(this.historic.color);
        } else {
            this.CalculatingMachine.setSource(this.source);
            const [point] = this.CalculatingMachine.comparation({
                series: this.series,
                companyId: this.companyId,
                xFilter: this.currentPeriod,
                yFilter: this.currentComparation
            });
            this.polarityColor = point?.polarityColor;
            this.color = this.ColorService.colorByPath(this.polarityColor);
        }
    }

    _setVisualization = () => {
        if (!_.get(this.series, "length")) return;
        const _formatLabel = (x) => {
            const period = this.currentPeriod.period;
            if (["yearly", "quarterly", "semester"].includes(period)) {
                return this.AvailablePeriod.getPeriodLabels(x, period);
            }
            return DateUtils.dateToString(x, "MMMM 'de' yyyy");
        };
        const _formatValue = this.FormatUnits.formatValue;
        this._setColor();
        let needReversed = false;
        if (this.series.length == 1) {
            needReversed =
                _.get(this.series[0], "metadata.natureOfAccount") == "negative"
                    ? true
                    : false;
        }

        const seriesReferences = (type) => {
            const color = this.historic?.color || this.polarityColor;
            if (this.goals && this.visualizationData?.historic) {
                return this.visualizationData?.historic.map((serieChart) => {
                    const serie = this.series.find(
                        (s) => s._id == serieChart.dataSerieId
                    );
                    return { _id: serie._id, type, color, name: serie.name };
                });
            } else {
                return this.series.map((s) => {
                    return { _id: s._id, type, color, name: s.name };
                });
            }
        };

        const categories = {
            areaspline: {
                category: "areaspline",
                chart: {
                    reflow: true,
                    type: "areaspline",
                    margin: 0,
                    backgroundColor: null,

                    spacingBottom: 0
                },
                legend: {
                    enabled: false
                },
                plotOptions: {
                    areaspline: {
                        fillOpacity: 1
                    },
                    series: {
                        marker: {
                            enabled: true,
                            radius: 2
                        },
                        animation: {
                            duration: 1000
                        },
                        fillOpacity: 0.66,
                        states: {
                            hover: {
                                enabled: true,
                                fillOpacity: 1
                            }
                        },
                        color: this.color
                    }
                },

                yAxis: {
                    reversed: needReversed,
                    labels: {
                        enabled: false
                    },
                    title: {
                        text: ""
                    },
                    gridLineWidth: 0
                },
                xAxis: {
                    labels: {
                        enabled: false
                    },
                    lineWidth: 0,
                    tickLength: 0,
                    minPadding: 0,
                    maxPadding: 0
                },
                tooltip: {
                    formatter: function () {
                        if (this.series) {
                            const unit = this.series.userOptions.unit;

                            return `
                                <div class="tooltip-container" layout="row" layout-align="center center">
                                    <div class="tooltip-legend">
                                    ${_formatLabel(this.x)}: ${_formatValue(
                                unit,
                                this.y
                            )}
                                    </div>
                                </div>
                        `;
                        }
                    },
                    backgroundColor: "rgba(255, 255, 255, 1)",
                    useHTML: true,
                    followPointer: true
                }
            },
            column: {
                category: "column",
                plotOptions: {
                    series: {
                        borderWidth: 0,
                        marker: {
                            enabled: true,
                            radius: 2
                        },
                        animation: {
                            duration: 1000
                        },
                        fillOpacity: 0.66,
                        states: {
                            hover: {
                                enabled: true,
                                fillOpacity: 1
                            }
                        }
                    }
                },
                series: seriesReferences("column"),
                chart: {
                    reflow: true,
                    type: "column"
                },
                legend: {
                    enabled: false
                },
                yAxis: {
                    reversed: needReversed,
                    title: {
                        enabled: false
                    },
                    plotLines: [
                        {
                            width: 1,
                            value: 0,
                            zIndex: 6
                        }
                    ],
                    labels: {
                        enabled: true
                    }
                }
            },
            line: {
                category: "line",
                chart: {
                    type: "line",
                    reflow: true
                },
                legend: {
                    enabled: false
                },
                plotOptions: {
                    series: {
                        color: this.color
                    }
                },
                yAxis: {
                    title: {
                        enabled: false
                    },
                    plotLines: [
                        {
                            width: 1,
                            value: 0,
                            zIndex: 6
                        }
                    ],
                    labels: {
                        enabled: true,
                        format: "{value}"
                    }
                },
                series: seriesReferences("line")
            }
        };
        const chartModel = categories[this.chartCategory];
        if (this.goals && this.visualizationData?.goals?.length) {
            chartModel.goals = this.visualizationData?.goals.map((goal) => {
                const color = this.ColorService.getLowerTonality(
                    chartModel?.series?.[0]?.color || "goals.medium"
                );
                return {
                    _id: goal.r,
                    color: goal.color || color,
                    type: "line",
                    dashStyle: goal.dashStyle || "ShortDash"
                };
            });
        }

        this.visualization = {
            chart: chartModel
        };
    };
}

ChartHistoricComponent.$inject = ["$state", "$filter", "ThemesService"];

export { ChartHistoricComponent };

const name = "chartHistoricComponent";
const def = {
    bindings: {
        series: "<",
        points: "<",
        currentPeriod: "<",
        currentComparation: "<",
        source: "<",
        chartCategory: "@",
        fakeData: "<",
        historic: "<",
        projection: "<",
        budget: "<",
        goals: "<",
        visualizationData: "<"
    },
    controller: ChartHistoricComponent,
    template
};
angular.module("nucont.shared").component(name, def);
export default { name, ...def };
